import React, { useState, useMemo, useEffect, useRef } from 'react'
// @ts-ignore
import { Badge, Button, InputGroup, Checkbox } from 'dsm-react-lib'
import {
  IconSearch,
  IconChevronDown,
  IconAlertTriangle,
  IconGrid4x4 as GridIcon,
  IconList as ListIcon,
  IconDatabase,
  IconDownload,
  IconFiles,
  IconTrash
} from '@tabler/icons-react'
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Spinner } from 'reactstrap'
import Skeleton from 'react-loading-skeleton'
import { FilesIcon, DataProductsIcon, PendingIcon } from '../Chatbot/icons/Icons'
import { DataboxItemData } from './SampleTableData'
import { StyledDataTable } from '../Applications/styles'
import '@styles/react/libs/tables/react-dataTable-component.scss'
import './styles.css'
import DataboxItem from './DataboxItems'
import { useDataboxService } from './services/databoxService'
import { getFileIcon } from '../Chatbot/utils/helpers'
import { toast } from 'react-toastify'

const MAX_INDIVIDUAL_SELECTION = 100
const UNAVAILABLE_DOC_TOOLTIP =
  "This document hasn't been uploaded successfully so is not available for chat"

// Helper to create avatar data
const createAvatarData = (name: string) => ({
  name,
  avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(
    name
  )}&background=007bff&color=fff&size=32`
})

interface DataboxDataGridProps {
  databoxId?: string
  smartDocsDataboxId?: string
  databoxFiles?: any[]
  databoxDataProducts?: any[]
  filesCount?: number | undefined
  dataProductsCount?: number | undefined
  userRole: 'Owner' | 'Contributor' | 'Member'
  showDeleteButton?: boolean
  readOnly?: boolean
  isFetchingDataboxDetails?: boolean
  databoxDetails?: any
  shouldRefetchDataProducts?: boolean
  selectedItemIds?: string[]
  preSelectedItems?: DataboxItemData[]
  lockedDataboxIds?: string[]
  onDeleteItem: (item: DataboxItemData) => void
  onBulkDelete: (items: DataboxItemData[]) => void
  onDownloadFile: (file: any) => void
  onUploadFiles: () => void
  onAddDataProducts: () => void
  onSelectionChange?: (selectedItems: DataboxItemData[]) => void
}

const DataboxDataGrid: React.FC<DataboxDataGridProps> = ({
  databoxId,
  smartDocsDataboxId,
  databoxFiles: databoxFilesProp = [],
  databoxDataProducts: databoxDataProductsProp = [],
  filesCount: filesCountProp,
  dataProductsCount: dataProductsCountProp,
  userRole,
  showDeleteButton = true,
  readOnly = false,
  isFetchingDataboxDetails: isFetchingDataboxDetailsProp = false,
  databoxDetails,
  shouldRefetchDataProducts = false,
  onDeleteItem,
  onBulkDelete,
  onDownloadFile,
  onUploadFiles,
  onAddDataProducts,
  onSelectionChange,
  selectedItemIds,
  preSelectedItems = [],
  lockedDataboxIds = []
}) => {
  const {
    getDataboxDocumentsPaginated,
    getDataboxDataProductsPaginated,
    isGettingDocumentsPaginated,
    isGettingDataProductsByDatabox
  } = useDataboxService()

  // Consolidated pagination state - stores API-fetched data when browsing a specific databox
  const [paginatedData, setPaginatedData] = useState({
    files: [] as any[],
    dataProducts: [] as any[]
  })
  const [pagination, setPagination] = useState({
    files: { currentPage: 1, rowsPerPage: 50 },
    dataProducts: { currentPage: 1, rowsPerPage: 50 }
  })
  const [totalCounts, setTotalCounts] = useState({ files: 0, dataProducts: 0 })
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Overflow buffer for the "All" tab: files that don't fit alongside data products on a page
  // are held here and shown first on the next page instead of being dropped.
  const fileOverflowRef = useRef<any[]>([])
  // Overflow carried in before the current page's files were merged, so re-running the split for the same page doesn't re-add it.
  const carriedOverflowRef = useRef<any[]>([])
  const processedFilesRef = useRef<any[] | null>(null)
  const [allTabFiles, setAllTabFiles] = useState<any[]>([])

  // The table's own scrollable area
  const tableScrollRef = useRef<HTMLDivElement>(null)

  // Choose data source: use paginated API data when browsing (databoxId exists), otherwise use parent props
  // useMemo caches the result to avoid recalculation on every render
  const resolvedFiles = useMemo(
    () => (databoxId ? paginatedData.files : databoxFilesProp),
    [databoxId, paginatedData.files, databoxFilesProp]
  )
  const resolvedDataProducts = useMemo(
    () => (databoxId ? paginatedData.dataProducts : databoxDataProductsProp),
    [databoxId, paginatedData.dataProducts, databoxDataProductsProp]
  )
  const resolvedFilesCount = useMemo(
    () => (databoxId ? databoxDetails?.[databoxId]?.filesCount : filesCountProp),
    [databoxId, databoxDetails, filesCountProp]
  )
  const resolvedDataProductsCount = useMemo(
    () => (databoxId ? databoxDetails?.[databoxId]?.dataProductsCount : dataProductsCountProp),
    [databoxId, databoxDetails, dataProductsCountProp]
  )

  const isFetchingDataboxDetails = databoxId
    ? isGettingDocumentsPaginated || isGettingDataProductsByDatabox
    : isFetchingDataboxDetailsProp
  const isDataboxLocked =
    !!databoxId && lockedDataboxIds.map(String).includes(String(smartDocsDataboxId))
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')
  const [selectedRows, setSelectedRows] = useState<string[]>(selectedItemIds ?? [])
  // Cache to store full item data for selected items across pages
  const [selectedItemsCache, setSelectedItemsCache] = useState<Map<string, DataboxItemData>>(
    new Map()
  )
  const [selectedTab, setSelectedTab] = useState('All')
  const [viewType, setViewType] = useState<'list' | 'grid'>('list')
  const [iconHoverIdx, setIconHoverIdx] = useState<number | null>(null)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const [tableDensity, setTableDensity] = useState<'comfortable' | 'compact' | 'spacious'>(
    'compact'
  )

  // Initialize cache with pre-selected items (for edit mode)
  useEffect(() => {
    if (preSelectedItems.length > 0) {
      setSelectedItemsCache((prevCache) => {
        const newCache = new Map(prevCache)
        preSelectedItems.forEach((item) => {
          if (!newCache.has(item.id)) {
            newCache.set(item.id, item)
          }
        })
        return newCache
      })
    }
  }, [preSelectedItems])

  // Reset pagination when tab changes
  useEffect(() => {
    setPagination({
      files: { currentPage: 1, rowsPerPage: 50 },
      dataProducts: { currentPage: 1, rowsPerPage: 50 }
    })
    fileOverflowRef.current = []
    processedFilesRef.current = null
    setAllTabFiles([])
  }, [selectedTab])

  // Debounce search value - update after 0.5 seconds of inactivity
  useEffect(() => {
    const timer = setTimeout(() => {
      // Nothing to reset when the search text hasn't changed (also the case right after mount)
      if (searchValue === debouncedSearchValue) return
      setDebouncedSearchValue(searchValue)
      // Reset to page 1 when search changes
      setPagination({
        files: { currentPage: 1, rowsPerPage: pagination.files.rowsPerPage },
        dataProducts: { currentPage: 1, rowsPerPage: pagination.dataProducts.rowsPerPage }
      })
      fileOverflowRef.current = []
      processedFilesRef.current = null
      setAllTabFiles([])
    }, 500)

    return () => clearTimeout(timer)
  }, [searchValue])

  // Fetch files when databoxId, tab, or files pagination changes
  useEffect(() => {
    if (!databoxId) return
    if (selectedTab === 'All' || selectedTab === 'Files') {
      const load = async () => {
        setPaginatedData((prev) => ({ ...prev, files: [] }))
        setIsLoadingMore(true)
        const response = await getDataboxDocumentsPaginated(
          databoxId,
          pagination.files.currentPage,
          pagination.files.rowsPerPage,
          debouncedSearchValue || undefined,
          'uploadTime',
          'desc'
        )
        if (response && Array.isArray(response)) {
          setPaginatedData((prev) => ({ ...prev, files: response }))
          if (databoxDetails?.[databoxId]?.filesCount !== undefined) {
            setTotalCounts((prev) => ({ ...prev, files: databoxDetails[databoxId].filesCount }))
          }
        }
        setIsLoadingMore(false)
      }
      load()
    }
  }, [
    databoxId,
    selectedTab,
    pagination.files.currentPage,
    pagination.files.rowsPerPage,
    debouncedSearchValue
  ])

  // Splits fetched files between this page's "All" tab (leaving room for data products on
  // page 1) and the overflow buffer, so files + data products fit exactly rowsPerPage per page.
  useEffect(() => {
    if (selectedTab !== 'All' || !databoxId) return
    if (processedFilesRef.current !== paginatedData.files) {
      carriedOverflowRef.current = fileOverflowRef.current
      processedFilesRef.current = paginatedData.files
    }
    const productsReserved = pagination.files.currentPage === 1 ? resolvedDataProducts.length : 0
    const budget = Math.max(pagination.files.rowsPerPage - productsReserved, 0)
    const available = [...carriedOverflowRef.current, ...paginatedData.files]
    setAllTabFiles(available.slice(0, budget))
    fileOverflowRef.current = available.slice(budget)
  }, [
    selectedTab,
    paginatedData.files,
    resolvedDataProducts.length,
    pagination.files.currentPage,
    pagination.files.rowsPerPage
  ])

  // Fetch data products when databoxId, tab, or data products pagination changes
  useEffect(() => {
    if (!databoxId) return
    if (selectedTab === 'All') {
      // On "All" tab, only fetch data products on first page
      const load = async () => {
        if (pagination.files.currentPage === 1) {
          setPaginatedData((prev) => ({ ...prev, dataProducts: [] }))
          setIsLoadingMore(true)
          const response = await getDataboxDataProductsPaginated(
            databoxId,
            1,
            pagination.files.rowsPerPage,
            debouncedSearchValue || undefined
          )
          if (response && Array.isArray(response)) {
            setPaginatedData((prev) => ({ ...prev, dataProducts: response }))
            if (databoxDetails?.[databoxId]?.dataProductsCount !== undefined) {
              setTotalCounts((prev) => ({
                ...prev,
                dataProducts: databoxDetails[databoxId].dataProductsCount
              }))
            }
          }
          setIsLoadingMore(false)
        } else {
          // Clear data products on other pages
          setPaginatedData((prev) => ({ ...prev, dataProducts: [] }))
        }
      }
      load()
    } else if (selectedTab === 'Data Products') {
      const load = async () => {
        setPaginatedData((prev) => ({ ...prev, dataProducts: [] }))
        setIsLoadingMore(true)
        const response = await getDataboxDataProductsPaginated(
          databoxId,
          pagination.dataProducts.currentPage,
          pagination.dataProducts.rowsPerPage,
          debouncedSearchValue || undefined
        )
        if (response && Array.isArray(response)) {
          setPaginatedData((prev) => ({ ...prev, dataProducts: response }))
          if (databoxDetails?.[databoxId]?.dataProductsCount !== undefined) {
            setTotalCounts((prev) => ({
              ...prev,
              dataProducts: databoxDetails[databoxId].dataProductsCount
            }))
          }
        }
        setIsLoadingMore(false)
      }
      load()
    }
  }, [
    databoxId,
    selectedTab,
    pagination.files.currentPage,
    pagination.dataProducts.currentPage,
    pagination.dataProducts.rowsPerPage,
    debouncedSearchValue
  ])

  // Filter and transform raw data based on selected tab and search value
  const filteredData = useMemo(() => {
    let tabFilteredData: DataboxItemData[] = []
    if (selectedTab === 'All') {
      const dataProductItems: DataboxItemData[] = resolvedDataProducts.map(
        (dp: any, index: number): DataboxItemData => ({
          // Same id priority as the Data Products tab below, for consistent selection
          // Always a string - the backend can send numbers, which breaks id lookups later
          id: String(
            dp.dataProductExternalId || dp.associationId || dp.externalId || dp.id || `dp-${index}`
          ),
          // Real product id for persisting/re-fetching, separate from the display id above
          dataProductId: dp.dataProductExternalId || dp.id,
          name: dp.name || '-',
          type: 'dataproduct',
          source: '-',
          owner: dp.owners?.[0] || '-',
          dateAdded: dp.associatedAt,
          tags: dp.tags || [],
          status: 'active',
          tier: dp.tier || 'Source'
        })
      )

      const fileItems: DataboxItemData[] = (databoxId ? allTabFiles : resolvedFiles).map(
        (file: any, index: number): DataboxItemData => ({
          // Some databox APIs reuse the same placeholder externalId for every file - prefer
          // the file's own id so rows/checkboxes don't collapse together. Always a string.
          id: String(file.id || file.externalId || `file-${index}`),
          name: file.name,
          type: 'file' as const,
          source: '-',
          owner: file.uploadedBy || '-',
          dateAdded: file.uploadedDate,
          tags: file.tags || [],
          status: file.status,
          smartDocsDocumentId: file.id || file.smartDocsDocumentId, // Use numeric ID from API
          cognitiveSearchId: file.cognitiveSearchId
        })
      )

      tabFilteredData = [...dataProductItems, ...fileItems]
    } else if (selectedTab === 'Files') {
      tabFilteredData = resolvedFiles.map(
        (file: any, index: number): DataboxItemData => ({
          // Prefer the file's own id - some databox APIs reuse the same placeholder externalId
          id: String(file.id || file.externalId || `file-${index}`),
          name: file.name,
          type: 'file',
          source: 'SmartDocs',
          owner: file.uploadedBy || '-',
          dateAdded: file.uploadedDate,
          smartDocsDocumentId: file.id || file.smartDocsDocumentId, // Use numeric ID from API
          tags: file.tags || [],
          status: file.status,
          cognitiveSearchId: file.cognitiveSearchId
        })
      )
    } else if (selectedTab === 'Data Products') {
      if (resolvedDataProducts.length > 0) {
        tabFilteredData = resolvedDataProducts.map(
          (dp: any, index: number): DataboxItemData => ({
            // Always a string, same as the 'All' tab above
            id: String(
              dp.dataProductExternalId ||
                dp.associationId ||
                dp.externalId ||
                dp.id ||
                `dp-${index}`
            ),
            dataProductId: dp.dataProductExternalId || dp.id,
            name: dp.name || '-',
            type: 'dataproduct',
            source: '-',
            owner: dp.owners?.[0] || '-',
            dateAdded: dp.associatedAt,
            tags: dp.tags || [],
            status: dp.isDiscoverable ? 'active' : 'pending',
            tier: dp.tier || 'Source'
          })
        )
      }
    }

    if (searchValue.length) {
      const searchLower = searchValue.toLowerCase()
      const tagName = (tag: any) => (typeof tag === 'string' ? tag : tag.name || '')

      return tabFilteredData.filter(
        (item) =>
          item.name.toLowerCase().includes(searchLower) ||
          item.source.toLowerCase().includes(searchLower) ||
          item.owner.toLowerCase().includes(searchLower) ||
          item.tags.some((tag) => tagName(tag).toLowerCase().includes(searchLower))
      )
    }

    return tabFilteredData
  }, [resolvedFiles, resolvedDataProducts, selectedTab, searchValue, allTabFiles, databoxId])

  // Update cache with current page items
  useEffect(() => {
    setSelectedItemsCache((prevCache) => {
      let hasChanges = false
      const newCache = new Map(prevCache)
      filteredData.forEach((item) => {
        if (!newCache.has(item.id)) {
          newCache.set(item.id, item)
          hasChanges = true
        }
      })
      return hasChanges ? newCache : prevCache
    })
  }, [filteredData])

  // Helper to check if a row is selected
  const isRowSelected = (row: DataboxItemData) => {
    if (selectedRows.includes(row.id)) return true
    // For files, also check if smartDocsDocumentId is in selectedRows
    if (row.type === 'file' && row.smartDocsDocumentId) {
      return selectedRows.includes(String(row.smartDocsDocumentId))
    }
    return false
  }

  // Files that haven't finished indexing yet aren't usable for chat - the whole row is disabled
  const isRowUnavailable = (row: any) => row.type === 'file' && row.status !== 'Indexed'

  // Sync selectedRows when parent explicitly updates selectedItemIds
  useEffect(() => {
    if (selectedItemIds !== undefined) {
      const mappedIds: string[] = []

      selectedItemIds.forEach((selectedId) => {
        // Try to find matching row - check both id and smartDocsDocumentId for files
        const match = filteredData.find((row) => {
          if (row.id === selectedId) return true
          // For files, also check if selectedId matches smartDocsDocumentId
          if (row.type === 'file' && String(row.smartDocsDocumentId) === String(selectedId)) {
            return true
          }
          return false
        })

        if (match) {
          // Item is on current page, use its grid ID
          mappedIds.push(match.id)
          return
        }

        // No direct match - fall back to matching by name (a data product's id can differ
        // between fetches). Only trust an unambiguous, non-placeholder name match.
        const originalItem = preSelectedItems.find((item) => item.id === selectedId)
        const hasUsableName = originalItem?.name && originalItem.name !== '-'
        const nameMatches = hasUsableName
          ? filteredData.filter(
              (row) => row.type === originalItem.type && row.name === originalItem.name
            )
          : []
        const nameMatch = nameMatches.length === 1 ? nameMatches[0] : undefined

        mappedIds.push(nameMatch ? nameMatch.id : String(selectedId))
      })

      // Only update selectedRows if it actually changed, to avoid an extra re-render.
      const uniqueMappedIds = [...new Set(mappedIds)]
      setSelectedRows((prev) => {
        const isUnchanged =
          prev.length === uniqueMappedIds.length &&
          prev.every((id, index) => id === uniqueMappedIds[index])
        return isUnchanged ? prev : uniqueMappedIds
      })
    }
  }, [selectedItemIds, preSelectedItems, filteredData])

  const handleSelectAll = () => {
    const selectableData = filteredData.filter((row) => !isRowUnavailable(row) && !isDataboxLocked)
    const currentPageIds = selectableData.map((row) => row.id)
    const allCurrentPageSelected = selectableData.every((row) => isRowSelected(row))

    if (allCurrentPageSelected) {
      // Deselect all items on current page
      // Remove both the externalId and smartDocsDocumentId from selections
      const idsToRemove = new Set<string>()
      filteredData.forEach((row) => {
        idsToRemove.add(row.id)
        if (row.type === 'file' && row.smartDocsDocumentId) {
          idsToRemove.add(String(row.smartDocsDocumentId))
        }
      })

      const newSelectedRows = selectedRows.filter((id) => !idsToRemove.has(id))
      setSelectedRows(newSelectedRows)
      // Build the new cache first, then tell the parent - avoids updating it mid-render
      const newCache = new Map(selectedItemsCache)
      currentPageIds.forEach((id) => newCache.delete(id))

      const selectedItems: DataboxItemData[] = []
      newSelectedRows.forEach((id) => {
        const cachedItem = newCache.get(id)
        if (cachedItem) {
          selectedItems.push(cachedItem)
        } else {
          // Try to find in current filteredData
          const item = filteredData.find((item) => item.id === id)
          if (item) {
            selectedItems.push(item)
            newCache.set(id, item)
          }
        }
      })

      setSelectedItemsCache(newCache)
      onSelectionChange?.(selectedItems)
    } else {
      // Select all items on current page (append to existing selections)
      const newlySelectedIds = currentPageIds.filter((id) => !selectedRows.includes(id))
      const combinedSelection = [...selectedRows, ...newlySelectedIds]

      // Check if adding current page would exceed limit
      if (combinedSelection.length > MAX_INDIVIDUAL_SELECTION) {
        toast.error(
          `Selection limit reached. You can only select up to ${MAX_INDIVIDUAL_SELECTION} items in total. Currently ${selectedRows.length} items are selected.`,
          { autoClose: 5000 }
        )
        return
      }

      setSelectedRows(combinedSelection)
      // Same safe pattern as above
      const newCache = new Map(selectedItemsCache)
      filteredData.forEach((item) => {
        if (combinedSelection.includes(item.id)) {
          newCache.set(item.id, item)
        }
      })

      const selectedItems: DataboxItemData[] = []
      combinedSelection.forEach((id) => {
        const cachedItem = newCache.get(id)
        if (cachedItem) {
          selectedItems.push(cachedItem)
        } else {
          // Try to find in current filteredData
          const item = filteredData.find((item) => item.id === id)
          if (item) {
            selectedItems.push(item)
            newCache.set(id, item)
          }
        }
      })

      setSelectedItemsCache(newCache)
      onSelectionChange?.(selectedItems)
    }
  }

  const handleRowSelect = (id: string) => {
    const row = filteredData.find((item) => item.id === id)
    if (!row || isRowUnavailable(row) || isDataboxLocked) {
      return
    }
    let newSelectedRows: string[]

    if (selectedRows.includes(id)) {
      newSelectedRows = selectedRows.filter((rowId) => rowId !== id)
      setSelectedRows(newSelectedRows)
      // Same safe pattern as handleSelectAll above
      const newCache = new Map(selectedItemsCache)
      newCache.delete(id)

      const selectedItems: DataboxItemData[] = []
      newSelectedRows.forEach((rowId) => {
        const cachedItem = newCache.get(rowId)
        if (cachedItem) {
          selectedItems.push(cachedItem)
        } else {
          // Try to find in current filteredData
          const item = filteredData.find((item) => item.id === rowId)
          if (item) {
            selectedItems.push(item)
            newCache.set(rowId, item)
          }
        }
      })

      setSelectedItemsCache(newCache)
      onSelectionChange?.(selectedItems)
    } else {
      // Check if adding this item would exceed the limit
      if (selectedRows.length >= MAX_INDIVIDUAL_SELECTION) {
        toast.error(
          `Selection limit reached. You can only select up to ${MAX_INDIVIDUAL_SELECTION} items individually. To select more, please select the entire databox.`,
          { autoClose: 5000 }
        )
        return
      }
      newSelectedRows = [...selectedRows, id]
      setSelectedRows(newSelectedRows)
      // Same safe pattern as above
      const item = filteredData.find((item) => item.id === id)
      if (item) {
        const newCache = new Map(selectedItemsCache)
        newCache.set(id, item)

        const selectedItems: DataboxItemData[] = []
        newSelectedRows.forEach((rowId) => {
          const cachedItem = newCache.get(rowId)
          if (cachedItem) {
            selectedItems.push(cachedItem)
          } else {
            // Try to find in current filteredData
            const foundItem = filteredData.find((item) => item.id === rowId)
            if (foundItem) {
              selectedItems.push(foundItem)
              newCache.set(rowId, foundItem)
            }
          }
        })

        setSelectedItemsCache(newCache)
        onSelectionChange?.(selectedItems)
      }
    }
  }

  const getRowHeight = () => {
    switch (tableDensity) {
      case 'compact':
        return { height: 30, className: 'table-compact' }
      case 'spacious':
        return { height: 50, className: 'table-spacious' }
      default:
        return { height: 30, className: 'table-comfortable' }
    }
  }

  const columns: any[] = [
    {
      name: (
        <>
          {userRole !== 'Member' && (
            <Checkbox
              type="checkbox"
              name="select-all"
              id="select-all"
              checked={
                filteredData.filter((row) => !isRowUnavailable(row) && !isDataboxLocked).length >
                  0 &&
                filteredData
                  .filter((row) => !isRowUnavailable(row) && !isDataboxLocked)
                  .every((row) => isRowSelected(row))
              }
              disabled={isDataboxLocked || filteredData.every((row) => isRowUnavailable(row))}
              onChange={() => handleSelectAll()}
              indeterminate={
                selectedRows.length > 0 &&
                filteredData.some(
                  (row) => !isRowUnavailable(row) && !isDataboxLocked && isRowSelected(row)
                ) &&
                !filteredData
                  .filter((row) => !isRowUnavailable(row) && !isDataboxLocked)
                  .every((row) => isRowSelected(row))
              }
            />
          )}
        </>
      ),
      cell: (row: any) => {
        const isUnavailable = isRowUnavailable(row)
        const isLocked = isDataboxLocked
        return (
          <>
            {userRole !== 'Member' && (
              <Checkbox
                type="checkbox"
                name={`select-row-${row.id}`}
                id={`select-row-${row.id}`}
                checked={isRowSelected(row)}
                disabled={isUnavailable || isLocked}
                title={`${
                  isUnavailable
                    ? UNAVAILABLE_DOC_TOOLTIP
                    : isLocked
                    ? 'Cannot modify agent level document selection'
                    : ''
                }`}
                onChange={() => !isUnavailable && !isLocked && handleRowSelect(row.id)}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              />
            )}
          </>
        )
      },
      sortable: false,
      width: '35px'
    },
    {
      name: 'Name',
      selector: (row: any, rowIndex?: number) => row.name,
      sortable: true,
      width: '400px',
      cell: (row: any) => {
        const isUnavailable = isRowUnavailable(row)
        const isLocked = isDataboxLocked
        const isSelectable = userRole !== 'Member' && !isUnavailable && !isLocked
        return (
          <div
            className={`flex align-items-center justify-content-between w-full ${
              isSelectable
                ? 'cursor-pointer'
                : isUnavailable || isLocked
                ? 'cursor-not-allowed'
                : ''
            }`}
            title={
              isUnavailable ? UNAVAILABLE_DOC_TOOLTIP : isLocked ? 'Locked by agent' : undefined
            }
            onClick={() => isSelectable && handleRowSelect(row.id)}>
            <div className="flex align-items-center">
              <div>
                {row.type === 'file' ? (
                  getFileIcon(row.name)
                ) : (
                  <IconDatabase size={20} strokeWidth={2} className="text-info" />
                )}
              </div>
              <div className="card-header-text ml-3 line-clamp-1" title={row.name}>
                {row.name}
              </div>
            </div>
            <span className="mr-2">
              {row.status === 'Uploaded' || row.status === 'Ingested' ? (
                <PendingIcon size={16} color="var(--color-warning)" />
              ) : isUnavailable ? (
                <IconAlertTriangle size={16} color="var(--color-danger)" />
              ) : null}
            </span>
          </div>
        )
      }
    },
    {
      name: 'Source',
      selector: (row: any, rowIndex?: number) => row.source,
      sortable: true,
      width: '150px',
      cell: (row: any) => {
        const isUnavailable = isRowUnavailable(row)
        const isLocked = isDataboxLocked
        const isSelectable = userRole !== 'Member' && !isUnavailable && !isLocked
        return (
          <div
            className={`theme-text-subheading w-full h-full ${
              isSelectable
                ? 'cursor-pointer'
                : isUnavailable || isLocked
                ? 'cursor-not-allowed'
                : ''
            }`}
            title={
              isUnavailable ? UNAVAILABLE_DOC_TOOLTIP : isLocked ? 'Locked by agent' : undefined
            }
            onClick={() => isSelectable && handleRowSelect(row.id)}>
            {row.source}
          </div>
        )
      }
    },
    {
      name: 'Owner/Uploaded By',
      selector: (row: any, rowIndex?: number) => row.owner,
      sortable: true,
      width: '250px',
      cell: (row: any) => {
        const isUnavailable = isRowUnavailable(row)
        const isLocked = isDataboxLocked
        const isSelectable = userRole !== 'Member' && !isUnavailable && !isLocked
        return (
          <div
            className={`flex align-items-center ${
              isSelectable
                ? 'cursor-pointer'
                : isUnavailable || isLocked
                ? 'cursor-not-allowed'
                : ''
            }`}
            title={
              isUnavailable ? UNAVAILABLE_DOC_TOOLTIP : isLocked ? 'Locked by agent' : undefined
            }
            onClick={() => isSelectable && handleRowSelect(row.id)}>
            <span className="theme-text-subheading">{row.owner}</span>
          </div>
        )
      }
    },
    {
      name: 'Date Added',
      selector: (row: any, rowIndex?: number) => row.dateAdded,
      sortable: true,
      width: '200px',
      cell: (row: any) => {
        const isUnavailable = isRowUnavailable(row)
        const isLocked = isDataboxLocked
        const isSelectable = userRole !== 'Member' && !isUnavailable && !isLocked
        return (
          <div
            className={`theme-text-subheading w-full h-full ${
              isSelectable
                ? 'cursor-pointer'
                : isUnavailable || isLocked
                ? 'cursor-not-allowed'
                : ''
            }`}
            title={
              isUnavailable ? UNAVAILABLE_DOC_TOOLTIP : isLocked ? 'Locked by agent' : undefined
            }
            onClick={() => isSelectable && handleRowSelect(row.id)}>
            {new Date(row.dateAdded).toLocaleDateString()}
          </div>
        )
      }
    },
    {
      name: 'Tags',
      selector: (row: any, rowIndex?: number) =>
        row?.tags
          ?.map((tag: any) => (typeof tag === 'object' ? tag?.name || tag?.label || '' : tag || ''))
          .filter(Boolean)
          .join(', '),
      sortable: false,
      minWidth: '150px',
      maxWidth: '350px',
      cell: (row: any) => {
        const isUnavailable = isRowUnavailable(row)
        const isLocked = isDataboxLocked
        const isSelectable = userRole !== 'Member' && !isUnavailable && !isLocked
        return (
          <div
            className={`flex flex-wrap items-center content-center ${
              isSelectable
                ? 'cursor-pointer'
                : isUnavailable || isLocked
                ? 'cursor-not-allowed'
                : ''
            }`}
            title={
              isUnavailable ? UNAVAILABLE_DOC_TOOLTIP : isLocked ? 'Locked by agent' : undefined
            }
            onClick={() => isSelectable && handleRowSelect(row.id)}>
            {row?.tags?.slice(0, 2).map((tag: any, index: number) => {
              const tagLabel = typeof tag === 'object' ? tag?.name || tag?.label || '' : tag || ''

              if (!tagLabel) {
                return null
              }

              return (
                <Badge
                  type="pill"
                  key={tag.id || tag.name || index}
                  label={tagLabel}
                  className="section-label mr-1 inline-flex h-6 items-center justify-center rounded-full theme-badge-tag px-1 font-medium text-center leading-[1]"
                />
              )
            })}
            {row?.tags?.length > 2 && (
              <Badge
                type="pill"
                label={
                  <span className="inline-flex h-full w-full items-center justify-center text-center leading-none">
                    +{row.tags.length - 2}
                  </span>
                }
                className="text-xs mb-0 inline-flex h-6 w-6 items-center justify-center rounded-full theme-badge-tag-overflow p-0 font-semibold text-center leading-none"
                variant="light"
                color="primary"
              />
            )}
          </div>
        )
      }
    }
  ]

  // Transform databox items to include avatar data
  const transformedFilesData = useMemo(
    () =>
      filteredData
        .filter((item) => item.type === 'file')
        .map((file) => {
          const apiFile = resolvedFiles.find(
            (f: any) => f.id === file.id || f.externalId === file.id
          )
          const ownerName = apiFile?.uploadedBy || file.owner
          return {
            ...file,
            name: apiFile?.name || file.name,
            owner: ownerName,
            dateAdded: apiFile?.uploadedDate || file.dateAdded,
            tags: apiFile?.tags || file.tags,
            status: apiFile?.status,
            smartDocsDocumentId: apiFile?.smartDocsDocumentId,
            creators: [createAvatarData(ownerName)]
          }
        }),
    [filteredData, resolvedFiles]
  )

  const transformedDataProductsData = useMemo(
    () =>
      filteredData
        .filter((item) => item.type === 'dataproduct')
        .map((dataProduct) => {
          const apiData = resolvedDataProducts.find(
            (dp: any) => dp.associationId === dataProduct.id || dp.id === dataProduct.id
          )
          const ownerName = apiData?.owners?.[0] || apiData?.associatedBy || dataProduct.owner
          return {
            ...dataProduct,
            ...(apiData && {
              name: apiData.name,
              description: apiData.description,
              source: '',
              owner: apiData.owners?.[0] || '-',
              dateAdded: apiData.associatedAt,
              tags: apiData.tags || []
            }),
            creators: [createAvatarData(ownerName)],
            type: dataProduct.type
          }
        }),
    [filteredData, resolvedDataProducts]
  )

  const transformedMixedData = useMemo(
    () =>
      filteredData.map((item) => {
        const apiFile =
          item.type === 'file'
            ? resolvedFiles.find(
                (f: any) =>
                  f.externalId === item.id || f.id === item.id || f.smartDocsDocumentId === item.id
              )
            : null

        return {
          ...item,
          ...(item.type === 'file' &&
            apiFile && {
              smartDocsDocumentId: apiFile.smartDocsDocumentId,
              actualFileId: apiFile.id
            }),
          creators: [createAvatarData(item.owner)],
          source: item.source,
          owner: item.owner,
          dateAdded: item.dateAdded,
          tags: item.tags,
          type: item.type,
          status: item.status
        }
      }),
    [filteredData, resolvedFiles]
  )

  // Get current tab's displayed data for both list and grid views
  const getCurrentTabData = (): DataboxItemData[] => {
    if (selectedTab === 'Files') {
      return filteredData.filter((item) => item.type === 'file')
    } else if (selectedTab === 'Data Products') {
      return filteredData.filter((item) => item.type === 'dataproduct')
    }
    return filteredData
  }

  const currentTabData = getCurrentTabData()

  // Handle page change for Files or All tab
  const handlePageChange = (page: number) => {
    if (selectedTab === 'Files' || selectedTab === 'All') {
      setPagination((prev) => ({
        ...prev,
        files: { ...prev.files, currentPage: page }
      }))
    }
  }

  // Handle page change for Data Products tab
  const handleDataProductsPageChange = (page: number) => {
    setPagination((prev) => ({
      ...prev,
      dataProducts: { ...prev.dataProducts, currentPage: page }
    }))
  }

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    if (selectedTab === 'Files' || selectedTab === 'All') {
      setPagination((prev) => ({
        ...prev,
        files: { currentPage: page, rowsPerPage: newPerPage }
      }))
    } else {
      setPagination((prev) => ({
        ...prev,
        dataProducts: { currentPage: page, rowsPerPage: newPerPage }
      }))
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Search and Actions Bar */}
      <div className="mb-[8px]">
        <div className="flex align-items-center mb-[8px] gap-2">
          <div className="flex align-items-center">
            {[
              { label: 'All', icon: null },
              {
                label: 'Files',
                icon:
                  selectedTab === 'Files' ? (
                    <FilesIcon color={'var(--color-primary)'} size={25} />
                  ) : (
                    <FilesIcon color={'var(--icon-muted)'} size={25} />
                  )
              },
              {
                label: 'Data Products',
                icon:
                  selectedTab === 'Data Products' ? (
                    <IconDatabase size={20} strokeWidth={2} />
                  ) : (
                    <DataProductsIcon size={25} />
                  )
              }
            ].map((tab) => (
              <button
                key={tab.label}
                type="button"
                className={`data-tab-view btn bg-transparent px-2 py-1 position-relative flex align-items-center text-base border-none rounded-none ${
                  selectedTab === tab.label
                    ? 'theme-selected-text tab-selected'
                    : 'theme-text-subheading border-b-3 border-transparent'
                }`}
                onClick={() => setSelectedTab(tab.label)}>
                {tab.icon}&nbsp;&nbsp;
                {tab.label}
                {tab.label === 'Files' && resolvedFilesCount !== undefined && (
                  <span className="ms-50 theme-text-subheading">({resolvedFilesCount})</span>
                )}
                {tab.label === 'Data Products' && resolvedDataProductsCount !== undefined && (
                  <span className="ms-50 theme-text-subheading">({resolvedDataProductsCount})</span>
                )}
              </button>
            ))}
          </div>
          {/* {userRole !== 'Member' && !readOnly && (
            <div className="ms-auto justify-content-end flex gap-2">
              {selectedTab !== 'Data Products' && (
                <Button
                  icon={<FilesIcon size={20} color="white" />}
                  label="Upload files"
                  color="primary"
                  variant="fill"
                  size="small"
                  onClick={onUploadFiles}
                />
              )}
              {selectedTab !== 'Files' && (
                <Button
                  icon={<IconDatabase size={20} color="white" />}
                  label="Add data products"
                  color="primary"
                  variant="fill"
                  size="small"
                  onClick={onAddDataProducts}
                />
              )}
            </div>
          )} */}
        </div>
        <div className="flex align-items-center justify-content-between w-100">
          {/* Search Input */}
          <div className="col-md-11 flex gap-2 align-items-center">
            <InputGroup
              addon={<IconSearch size={14} />}
              addonPosition="start"
              addonType="merged"
              placeholder="Search by files, products, owners, tags..."
              value={searchValue}
              onChange={(e: any) => setSearchValue(e.target.value)}
              className="w-50"
            />
            {/* Selection count indicator */}
            {selectedRows.length > 0 && userRole !== 'Member' && (
              <div
                className={`text-sm font-medium ${
                  selectedRows.length >= MAX_INDIVIDUAL_SELECTION ? 'text-warning' : 'text-primary'
                }`}>
                {selectedRows.length} / {MAX_INDIVIDUAL_SELECTION} selected
                {selectedRows.length >= MAX_INDIVIDUAL_SELECTION && (
                  <span className="text-warning ml-1">(Limit reached)</span>
                )}
              </div>
            )}
            {/* Bulk Delete Button - shown when items are selected */}
            {/* {showDeleteButton && selectedRows.length > 0 && (
              <Button
                icon={<IconTrash size={16} />}
                label={`Delete (${selectedRows.length})`}
                color="danger"
                variant="outline"
                size="small"
                className="whitespace-nowrap"
                onClick={() => {
                  const items = filteredData.filter((item) => selectedRows.includes(item.id))
                  onBulkDelete(items)
                  setSelectedRows([])
                  setSelectAll(false)
                }}
              />
            )} */}
          </div>
          {/* <div className="flex">
            <Button
              onClick={() => setViewType('list')}
              title="List View"
              variant="flat"
              className={`setViewTypeBtn p-[2px] ${
                viewType === 'list' ? 'text-primary' : 'text-muted'
              }`}>
              <ListIcon size={24} color={viewType === 'list' ? 'var(--color-primary)' : 'var(--icon-muted)'} />
            </Button>
            <Button
              label=""
              variant="flat"
              onClick={() => setViewType('grid')}
              title="Grid View"
              className={`setViewTypeBtn me-0 p-0 ${
                viewType === 'grid' ? 'text-primary' : 'text-muted'
              }`}>
              <GridIcon size={20} color={viewType === 'grid' ? 'var(--color-primary)' : 'var(--icon-muted)'} />
            </Button>
          </div> */}
        </div>
        {isDataboxLocked && (
          <div className="theme-badge-danger flex items-start gap-50 text-sm rounded-lg p-1 mt-50">
            <IconAlertTriangle size={20} className="flex-shrink-0 mt-0.5" />
            <span className="font-bold text-base">
              This databox is set at agent level and cannot be modified.
            </span>
          </div>
        )}
      </div>

      {/* Data Table or Card View */}
      <div ref={tableScrollRef} className="flex-1 overflow-y-auto overflow-x-auto min-h-0">
        {isLoadingMore && databoxId && (
          <div className="w-full">
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-1 border-b theme-border-default">
                {/* Checkbox column */}
                <div className="w-[30px] flex-shrink-0">
                  <Skeleton width={20} height={20} />
                </div>
                {/* Name column */}
                <div className="w-[400px] flex items-center gap-1">
                  <Skeleton circle width={24} height={24} />
                  <div className="flex-1">
                    <Skeleton width="85%" height={16} />
                  </div>
                </div>
                {/* Source column */}
                <div className="w-[150px]">
                  <Skeleton width="70%" height={16} />
                </div>
                {/* Owner/Uploaded By column */}
                <div className="w-[250px]">
                  <Skeleton width="80%" height={16} />
                </div>
                {/* Date Added column */}
                <div className="w-[200px]">
                  <Skeleton width="75%" height={16} />
                </div>
                {/* Tags column */}
                <div className="w-[200px] flex gap-1">
                  <Skeleton width={60} height={24} borderRadius={12} />
                  <Skeleton width={50} height={24} borderRadius={12} />
                </div>
              </div>
            ))}
          </div>
        )}

        {viewType === 'list' ? (
          (!isLoadingMore || !databoxId) &&
          (shouldRefetchDataProducts ||
            isFetchingDataboxDetails ||
            searchValue !== debouncedSearchValue) ? (
            <div className="w-full">
              {[...Array(8)].map((_, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-1 border-b theme-border-default">
                  {/* Checkbox column */}
                  <div className="w-[30px] flex-shrink-0">
                    <Skeleton width={20} height={20} />
                  </div>
                  {/* Name column */}
                  <div className="w-[400px] flex items-center gap-1">
                    <Skeleton circle width={24} height={24} />
                    <div className="flex-1">
                      <Skeleton width="85%" height={16} />
                    </div>
                  </div>
                  {/* Source column */}
                  <div className="w-[150px]">
                    <Skeleton width="70%" height={16} />
                  </div>
                  {/* Owner/Uploaded By column */}
                  <div className="w-[250px]">
                    <Skeleton width="80%" height={16} />
                  </div>
                  {/* Date Added column */}
                  <div className="w-[200px]">
                    <Skeleton width="75%" height={16} />
                  </div>
                  {/* Tags column */}
                  <div className="w-[200px] flex gap-1">
                    <Skeleton width={60} height={24} borderRadius={12} />
                    <Skeleton width={50} height={24} borderRadius={12} />
                  </div>
                </div>
              ))}
            </div>
          ) : currentTabData.length === 0 ? (
            <div className="text-center py-5">
              <p className="theme-text-subheading">No records to display</p>
            </div>
          ) : (
            <StyledDataTable
              className={`react-dataTable list-table ${getRowHeight().className}`}
              noHeader
              data={currentTabData}
              columns={columns}
              sortIcon={<IconChevronDown size={10} />}
              highlightOnHover
              pointerOnHover
              conditionalRowStyles={[
                {
                  when: (row: any) => isRowUnavailable(row),
                  style: {
                    cursor: 'not-allowed',
                    opacity: 0.5,
                    backgroundColor: 'var(--surface-muted)'
                  }
                }
              ]}
              pagination
              paginationServer={!!databoxId}
              paginationTotalRows={
                databoxId
                  ? selectedTab === 'Files'
                    ? totalCounts.files
                    : selectedTab === 'Data Products'
                    ? totalCounts.dataProducts
                    : totalCounts.files + totalCounts.dataProducts
                  : currentTabData.length
              }
              paginationDefaultPage={
                selectedTab === 'Data Products'
                  ? pagination.dataProducts.currentPage
                  : pagination.files.currentPage
              }
              paginationPerPage={
                selectedTab === 'Data Products'
                  ? pagination.dataProducts.rowsPerPage
                  : pagination.files.rowsPerPage
              }
              onChangePage={
                selectedTab === 'Data Products' ? handleDataProductsPageChange : handlePageChange
              }
              onChangeRowsPerPage={handlePerRowsChange}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              key={tableDensity}
              customStyles={{
                headRow: {
                  style: {
                    minHeight: `${getRowHeight().height}px`,
                    padding:
                      tableDensity === 'compact'
                        ? '2px 4px'
                        : tableDensity === 'spacious'
                        ? '8px 12px'
                        : '4px 8px'
                  }
                },
                rows: {
                  style: {
                    minHeight: `${getRowHeight().height}px`,
                    padding:
                      tableDensity === 'compact'
                        ? '2px 4px'
                        : tableDensity === 'spacious'
                        ? '8px 12px'
                        : '4px 8px'
                  } as any,
                  highlightOnHoverStyle: {
                    backgroundColor: 'var(--item-hover-bg)',
                    color: 'var(--text-heading)',
                    cursor: 'pointer',
                    transitionDuration: '0.15s',
                    outline: 'none',
                    outlineStyle: 'none',
                    outlineWidth: 0,
                    borderBottomColor: 'var(--theme-border)'
                  } as any
                },
                cells: {
                  style: {
                    height: `${getRowHeight().height}px`
                  }
                }
              }}
            />
          )
        ) : (
          <DataboxItem
            items={
              selectedTab === 'Files'
                ? transformedFilesData.filter(
                    (file): file is typeof file & { type: 'file' } => file.type === 'file'
                  )
                : selectedTab === 'Data Products'
                ? transformedDataProductsData
                    .filter((item) => item.type === 'dataproduct')
                    .map((item) => ({ ...item, type: 'dataproduct' }))
                : transformedMixedData.map((item) => ({
                    ...item,
                    owner: item.owner ?? '',
                    dateAdded: item.dateAdded ?? '',
                    tags: item.tags ?? [],
                    type: item.type ?? ''
                  }))
            }
            mode={
              selectedTab === 'Files'
                ? 'files'
                : selectedTab === 'Data Products'
                ? 'dataProducts'
                : 'mixed'
            }
            onDelete={(itemId) => {
              const item =
                selectedTab === 'Files'
                  ? transformedFilesData.find((f) => f.id === itemId)
                  : selectedTab === 'Data Products'
                  ? transformedDataProductsData.find((dp) => dp.id === itemId)
                  : transformedMixedData.find((i) => i.id === itemId)
              if (item) {
                onDeleteItem(item)
              }
            }}
            role={userRole || 'Member'}
          />
        )}
      </div>
    </div>
  )
}

export default React.memo(DataboxDataGrid)
